import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import type { PropsWithChildren } from 'react';

import { Arrow } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { formBunja, type InlineTooltipSide } from './bunja/form';

type SideTone = 'content.state.positive' | 'content.state.negative';

interface PreviewRowProps {
  side: 'long' | 'short';
  label: string;
  preview: InlineTooltipSide;
  pricePrecision: number;
}

/** PnL/ROI% sign → green/red (Price-mode whole-row profit/loss tint). */
export function signedValueTone(value: string): SideTone | undefined {
  const n = Num(value);
  if (n.isGreaterThan(0)) return 'content.state.positive';
  if (n.isLessThan(0)) return 'content.state.negative';
  return undefined;
}

/** Long = green, Short = red — label identity only (PnL/ROI preview). */
export function sideLabelTone(side: 'long' | 'short'): SideTone {
  return side === 'long' ? 'content.state.positive' : 'content.state.negative';
}

const PreviewRow = ({ side, label, preview, pricePrecision }: PreviewRowProps) => {
  if (preview == null) {
    return (
      <div className={hstack({ gap: 2, flexWrap: 'wrap' })}>
        <span className={css(rowLabel, { color: sideLabelTone(side) })}>{label}</span>
        <span className={css({ textStyle: 'Caption/L/Monospace/Regular', color: 'content.default.level-5' })}>-</span>
      </div>
    );
  }

  if (preview.kind === 'profit') {
    // Price mode: whole row follows profit/loss (one side loses).
    const tone = signedValueTone(preview.pnl);
    return (
      <div className={hstack({ gap: 2, flexWrap: 'wrap' })}>
        <span className={css(rowLabel, tone ? { color: tone } : {})}>{label}</span>
        <Formatted.Number
          value={preview.pnl}
          withPlus
          withColor
          toFixed
          precision={UsdtPrecision}
          css={{ textStyle: 'Caption/L/Monospace/Regular' }}
        />
        <Formatted.Percent
          value={preview.roi}
          withPlus
          withColor
          withParentheses
          toFixed
          precision={UsdtPrecision}
          css={{ textStyle: 'Caption/L/Monospace/Regular' }}
        />
      </div>
    );
  }

  // PnL/ROI mode: Long/Short label tint only; price + Change% stay neutral.
  return (
    <div className={hstack({ gap: 2, flexWrap: 'wrap' })}>
      <span className={css(rowLabel, { color: sideLabelTone(side) })}>{label}</span>
      <Formatted.Number
        value={preview.price}
        toFixed
        precision={pricePrecision}
        method='round-half-down'
        css={{ textStyle: 'Caption/L/Monospace/Regular', color: 'content.default.level-4' }}
      />
      <Formatted.Percent
        value={preview.changePercent}
        withPlus
        withParentheses
        toFixed
        precision={UsdtPrecision}
        css={{ textStyle: 'Caption/L/Monospace/Regular', color: 'content.default.level-4' }}
      />
    </div>
  );
};

interface TpSlEstPnlTooltipProps {
  field: 'tp' | 'sl';
}

const TpSlEstPnlTooltip = ({ field, children }: PropsWithChildren<TpSlEstPnlTooltipProps>) => {
  const { focusedTpSlFieldAtom } = useBunja(formBunja);
  const focusedField = useAtomValue(focusedTpSlFieldAtom);

  // tooltip atom은 라이브 가격 파생이라 매 tick 변한다. 포커스된(툴팁이 열린)
  // 입력칸에서만 구독하도록 패널을 별도 컴포넌트로 분리한다.
  const isOpen = focusedField === field;

  return (
    <div className={css({ pos: 'relative' })}>
      {children}
      {isOpen && <EstPnlPanel field={field} />}
    </div>
  );
};

export default TpSlEstPnlTooltip;

/** 툴팁이 열린 동안에만 마운트되어 preview를 구독한다. */
const EstPnlPanel = ({ field }: TpSlEstPnlTooltipProps) => {
  const t = useTranslations();
  const { tpslInlineTooltipAtom } = useBunja(formBunja);
  const tooltip = useAtomValue(tpslInlineTooltipAtom);
  const { symbolInfoWithDefaultAtom } = useBunja(tradeSymbolBunja);
  const { pricePrecision } = useAtomValue(symbolInfoWithDefaultAtom);

  const data = tooltip?.[field];
  const mode = data?.mode ?? 'price';
  const longLabel = mode === 'price' ? t('Trade.r260720.Long_profit') : t('Trade.r260720.Long_price');
  const shortLabel = mode === 'price' ? t('Trade.r260720.Short_profit') : t('Trade.r260720.Short_price');

  return (
    <div className={panel}>
      <div className={vstack({ gap: 2, alignItems: 'flex-start' })}>
        <PreviewRow side='long' label={longLabel} preview={data?.long ?? null} pricePrecision={pricePrecision} />
        <PreviewRow side='short' label={shortLabel} preview={data?.short ?? null} pricePrecision={pricePrecision} />
      </div>
      <div className={arrow}>
        <Arrow />
      </div>
    </div>
  );
};

const rowLabel = css.raw({
  textStyle: 'Caption/L/Monospace/Regular',
  flexShrink: 0,
  minW: 30,
});

const panel = css({
  pos: 'absolute',
  left: 0,
  bottom: '100%',
  zIndex: 'tooltip',
  py: 8,
  px: 12,
  rounded: 8,
  '--bg-color': '{colors.background.default.level-4}',
  bgColor: 'var(--bg-color)',
  color: 'content.default.level-5',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
});

const arrow = css({
  pos: 'absolute',
  top: 'calc(100% - 1px)',
  left: 16,
  lineHeight: 0,
});
